import { isUsableUrlTitle, type UrlEntity } from '../domain/note'
import { isPageKind, mapUrlPattern, type PageKind } from '../domain/urlCategory'
import { APP_SETTINGS } from '../config/appSettings'
import { resolveAppEnvironment, type AppEnvironment } from '../supabase/config'
import { getNativePlatform, type NativePlatform } from '../utils/nativeRuntime'

export interface LinkMetadata {
  title: string | null
  description: string | null
  imageUrl: string | null
  durationSeconds: number | null
  siteName: string | null
  author: string | null
  publishedAt: number | null
  pageKind?: PageKind
}

export interface LinkMetadataProvider {
  fetch(
    url: string,
    signal?: AbortSignal,
    options?: LinkMetadataFetchOptions,
  ): Promise<LinkMetadata>
}

export type LinkMetadataStatus = 'pending' | 'fetching' | 'waiting' | 'partial'

export interface LinkMetadataFetchOptions {
  /**
   * A previously durable response was useful but incomplete. This explicitly
   * asks the registry to coordinate one eligible refresh instead of returning
   * the same ready record forever.
   */
  refreshIncomplete?: boolean
}

export class LinkMetadataCoordinationError extends Error {
  readonly name = 'LinkMetadataCoordinationError'

  constructor(readonly retryAfterMs: number) {
    super('Link metadata is being prepared by another client.')
  }
}

export class LinkMetadataRemoteBackoffError extends Error {
  readonly name = 'LinkMetadataRemoteBackoffError'

  constructor(readonly retryAfterMs: number) {
    super('Link metadata is temporarily deferred by the shared registry.')
  }
}

/** Bump to re-enrich every previously saved URL after preview-quality rules change. */
export const LINK_METADATA_POLICY_VERSION = APP_SETTINGS.links.metadataPolicyVersion

interface LinkMetadataResponse extends Partial<LinkMetadata> {
  url?: string
}

function cleanText(value: unknown, maximumLength: number): string | null {
  if (typeof value !== 'string') return null
  const cleaned = value.replace(/\s+/g, ' ').trim()
  return cleaned ? cleaned.slice(0, maximumLength) : null
}

function cleanNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

function cleanHttpUrl(value: unknown): string | null {
  const cleaned = cleanText(value, 2_048)
  if (!cleaned) return null
  try {
    const parsed = new URL(cleaned)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null
  } catch {
    return null
  }
}

function retryDelay(response: Response, maximumMs: number): number | null {
  const seconds = Number(response.headers.get('Retry-After'))
  return Number.isFinite(seconds) && seconds > 0
    ? Math.min(Math.max(seconds * 1_000, 250), maximumMs)
    : null
}

export class HttpLinkMetadataProvider implements LinkMetadataProvider {
  constructor(private readonly apiBaseUrl: string) {}

  async fetch(
    url: string,
    signal?: AbortSignal,
    options: LinkMetadataFetchOptions = {},
  ): Promise<LinkMetadata> {
    const endpoint = new URL(`${this.apiBaseUrl}/api/links/preview`, 'https://api')
    endpoint.searchParams.set('url', url)
    if (options.refreshIncomplete) endpoint.searchParams.set('refresh', 'partial')
    const requestUrl = this.apiBaseUrl ? endpoint.toString() : `${endpoint.pathname}${endpoint.search}`
    const response = await fetch(requestUrl, { credentials: 'include', signal })
    const outcome = response.headers.get('X-Link-Preview-Outcome')
    if (outcome === 'backoff') {
      const retryAfterMs = retryDelay(response, APP_SETTINGS.links.metadataRemoteBackoffMaxMs)
      if (retryAfterMs !== null) throw new LinkMetadataRemoteBackoffError(retryAfterMs)
    }
    if (outcome === 'busy') {
      const retryAfterMs = retryDelay(response, APP_SETTINGS.links.metadataCoordinationRetryMaxMs)
      if (retryAfterMs !== null) throw new LinkMetadataCoordinationError(retryAfterMs)
    }
    // Remain compatible with an older Worker during a rolling deployment.
    const legacyRetryAfterMs = response.status === 503
      ? retryDelay(response, APP_SETTINGS.links.metadataCoordinationRetryMaxMs)
      : null
    if (legacyRetryAfterMs !== null) throw new LinkMetadataCoordinationError(legacyRetryAfterMs)
    if (!response.ok) throw new Error(`Link preview failed with ${response.status}.`)
    if (!response.headers.get('Content-Type')?.includes('application/json')) {
      throw new Error('Link preview returned an invalid response.')
    }
    const data = (await response.json()) as LinkMetadataResponse
    return {
      title: cleanText(data.title, 180),
      description: cleanText(data.description, 360),
      imageUrl: cleanHttpUrl(data.imageUrl),
      durationSeconds: cleanNumber(data.durationSeconds),
      siteName: cleanText(data.siteName, 100),
      author: cleanText(data.author, 100),
      publishedAt: cleanNumber(data.publishedAt),
      ...(isPageKind(data.pageKind) ? { pageKind: data.pageKind } : {}),
    }
  }
}

const BUILD_APP_ENVIRONMENT = import.meta.env.VITE_APP_ENV
const DEVELOPMENT_APP_API_ORIGIN = 'https://staging.app.minimany.app'
const PRODUCTION_APP_API_ORIGIN = 'https://app.minimany.app'

interface LinkMetadataApiBaseOptions {
  appEnvironment: AppEnvironment
  configuredBaseUrl?: string
  nativePlatform: NativePlatform | null
}

interface LinkMetadataPollingOptions {
  apiBaseUrl: string
  pageOrigin?: string
  sameOriginAvailable?: boolean
}

interface LinkPreviewImageUrlOptions extends LinkMetadataPollingOptions {}

export function resolveLinkMetadataApiBaseUrl({
  appEnvironment,
  configuredBaseUrl,
  nativePlatform,
}: LinkMetadataApiBaseOptions): string {
  if (!nativePlatform) return ''
  const configured = configuredBaseUrl?.trim()
  if (configured) {
    try {
      const url = new URL(configured)
      if (url.protocol === 'https:' && !url.username && !url.password) {
        return url.origin
      }
    } catch {
      // Invalid optional overrides fall back to the environment-owned app origin.
    }
  }
  // Vite replaces VITE_APP_ENV before minification. A production artifact therefore
  // keeps only the production origin instead of carrying an inactive staging branch.
  if (BUILD_APP_ENVIRONMENT === 'production') return PRODUCTION_APP_API_ORIGIN
  return appEnvironment === 'production'
    ? PRODUCTION_APP_API_ORIGIN
    : DEVELOPMENT_APP_API_ORIGIN
}

export function shouldPollLinkMetadata({ apiBaseUrl, pageOrigin, sameOriginAvailable }: LinkMetadataPollingOptions): boolean {
  if (apiBaseUrl || sameOriginAvailable) return true
  if (!pageOrigin) return true
  try {
    const hostname = new URL(pageOrigin).hostname.toLowerCase()
    return hostname !== 'localhost'
      && hostname !== '::1'
      && hostname !== '[::1]'
      && !hostname.startsWith('127.')
  } catch {
    return true
  }
}

export function resolveLinkPreviewImageUrl(
  imageUrl: string | null,
  { apiBaseUrl, pageOrigin, sameOriginAvailable }: LinkPreviewImageUrlOptions,
): string | null {
  if (!imageUrl || !shouldPollLinkMetadata({ apiBaseUrl, pageOrigin, sameOriginAvailable })) return imageUrl
  const baseUrl = apiBaseUrl || pageOrigin
  if (!baseUrl) return imageUrl
  try {
    const image = new URL(imageUrl)
    const existingProxySource = image.pathname === '/api/links/image'
      ? image.searchParams.get('url')
      : null
    if (image.pathname === '/api/links/image' && !existingProxySource) return imageUrl
    const sourceUrl = existingProxySource ?? imageUrl
    const endpoint = new URL('/api/links/image', baseUrl)
    endpoint.searchParams.set('url', sourceUrl)
    endpoint.searchParams.set('v', String(APP_SETTINGS.links.previewImageCacheVersion))
    return endpoint.toString()
  } catch {
    return imageUrl
  }
}

export function linkPreviewImageUrl(imageUrl: string | null): string | null {
  const apiBaseUrl = resolveLinkMetadataApiBaseUrl({
    appEnvironment: resolveAppEnvironment(import.meta.env.VITE_APP_ENV),
    configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
    nativePlatform: getNativePlatform(),
  })
  const pageOrigin = typeof window === 'undefined' ? undefined : window.location.origin
  return resolveLinkPreviewImageUrl(imageUrl, { apiBaseUrl, pageOrigin,
    sameOriginAvailable: import.meta.env.DEV && import.meta.env.VITE_LINK_PREVIEW_PROXY === 'true',
  })
}

export function createLinkMetadataProvider(): LinkMetadataProvider | null {
  const apiBaseUrl = resolveLinkMetadataApiBaseUrl({
    appEnvironment: resolveAppEnvironment(import.meta.env.VITE_APP_ENV),
    configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
    nativePlatform: getNativePlatform(),
  })
  const pageOrigin = typeof window === 'undefined' ? undefined : window.location.origin
  return shouldPollLinkMetadata({ apiBaseUrl, pageOrigin,
    sameOriginAvailable: import.meta.env.DEV && import.meta.env.VITE_LINK_PREVIEW_PROXY === 'true',
  })
    ? new HttpLinkMetadataProvider(apiBaseUrl)
    : null
}

export function isLinkMetadataIncomplete(
  metadata: Pick<LinkMetadata, 'imageUrl' | 'title'>,
  url?: UrlEntity,
): boolean {
  const genericTitle = Boolean(
    metadata.title && /^spotify(?:\s*[–—-]\s*web player)?$/i.test(metadata.title.trim()),
  )
  return metadata.imageUrl === null
    || genericTitle
    || Boolean(url && metadata.title && !isUsableUrlTitle(url, metadata.title))
    || Boolean(url && metadata.imageUrl && !hasSpecificLinkImage(url, metadata.imageUrl))
}

/** Parked domains keep their useful partial preview without retrying on every note open. */
export function shouldRetryLinkMetadataOnOpen(url: UrlEntity): boolean {
  if (!isLinkMetadataIncomplete(url, url)) return false
  let hostname = url.domain?.toLowerCase() ?? ''
  if (!hostname) {
    try {
      hostname = new URL(url.canonicalUrl ?? url.originalUrl).hostname.toLowerCase()
    } catch {
      return false
    }
  }
  hostname = hostname.replace(/^www\./, '')
  return !APP_SETTINGS.links.metadataRetryOnOpenHoldDomains.some((domain) => (
    hostname === domain || hostname.endsWith(`.${domain}`)
  ))
}

/**
 * A link image is evidence only when it represents the item, not a provider shell or the author's avatar.
 * Map snapshots cannot currently be proven to match a saved location, so use the
 * neutral place fallback until the provider returns location-bound imagery.
 */
export function hasSpecificLinkImage(url: UrlEntity, imageUrl: string | null): boolean {
  if (!imageUrl) return false
  if (mapUrlPattern(url)) return false

  const normalizedImage = imageUrl.toLowerCase()
  // A person's avatar (X's profile picture for a post with no media of its own) shows who, not the item.
  if (/\/profile_images\/|\/profile[-_]pic|[/_-]avatars?[/_.-]/.test(normalizedImage)) return false
  return !/(?:wikipedia[-_ ]?(?:logo|wordmark)|wikimedia-button|wiki(?:pedia)?[-_ ]?logo|(?:^|[/_-])(?:logo|icon|placeholder|default)(?:[._-]|$))/.test(normalizedImage)
}

function supportsProviderMetadataFallback(url: UrlEntity): boolean {
  let hostname = url.domain?.toLowerCase() ?? ''
  if (!hostname) {
    try {
      hostname = new URL(url.canonicalUrl ?? url.originalUrl).hostname.toLowerCase()
    } catch {
      return false
    }
  }
  hostname = hostname.replace(/^www\./, '')
  return hostname === 'youtu.be'
    || hostname === 'youtube.com'
    || hostname.endsWith('.youtube.com')
    || hostname === 'spotify.com'
    || hostname.endsWith('.spotify.com')
}

function linkMetadataRetryIntervalMs(url: UrlEntity): number {
  return supportsProviderMetadataFallback(url)
    ? APP_SETTINGS.links.providerFallbackRetryMs
    : APP_SETTINGS.links.incompleteMetadataRetryMs
}

function metadataRetryBackoffMs(baseMs: number, attemptCount: number): number {
  return baseMs * 2 ** Math.max(0, attemptCount - 1)
}

function retryAttemptCountForCurrentPolicy(url: UrlEntity): number {
  return url.metadataPolicyVersion === LINK_METADATA_POLICY_VERSION
    ? url.metadataFetchAttemptCount
    : 0
}

export function hasExhaustedLinkMetadataRetries(url: UrlEntity): boolean {
  const maximumAttempts = url.fetchedAt !== null && isLinkMetadataIncomplete(url, url)
    ? APP_SETTINGS.links.partialMetadataMaxAttempts
    : APP_SETTINGS.links.failedMetadataMaxAttempts
  return url.metadataFetchAttemptCount >= maximumAttempts
}

function failedMetadataRetryDelayMs(attemptCount: number): number {
  return metadataRetryBackoffMs(APP_SETTINGS.links.failedMetadataRetryBaseMs, attemptCount)
}

export function recordLinkMetadataFailure(url: UrlEntity, failedAt = Date.now()): UrlEntity {
  const metadataFetchAttemptCount = Math.min(
    retryAttemptCountForCurrentPolicy(url) + 1,
    url.fetchedAt !== null && isLinkMetadataIncomplete(url, url)
      ? APP_SETTINGS.links.partialMetadataMaxAttempts
      : APP_SETTINGS.links.failedMetadataMaxAttempts,
  )
  return {
    ...url,
    metadataPolicyVersion: LINK_METADATA_POLICY_VERSION,
    metadataFetchAttemptCount,
    metadataNextRetryAt: metadataFetchAttemptCount >= (
      url.fetchedAt !== null && isLinkMetadataIncomplete(url, url)
        ? APP_SETTINGS.links.partialMetadataMaxAttempts
        : APP_SETTINGS.links.failedMetadataMaxAttempts
    )
      ? null
      : failedAt + failedMetadataRetryDelayMs(metadataFetchAttemptCount),
  }
}

export function resetLinkMetadataRetry(url: UrlEntity): UrlEntity {
  return { ...url, metadataFetchAttemptCount: 0, metadataNextRetryAt: null }
}

export function deferLinkMetadataRetry(
  url: UrlEntity,
  retryAfterMs: number,
  deferredAt = Date.now(),
): UrlEntity {
  return {
    ...url,
    metadataPolicyVersion: LINK_METADATA_POLICY_VERSION,
    metadataFetchAttemptCount: retryAttemptCountForCurrentPolicy(url),
    metadataNextRetryAt: deferredAt + Math.max(0, retryAfterMs),
  }
}

export function needsLinkMetadataRefresh(url: UrlEntity, now = Date.now()): boolean {
  if (isLinkMetadataIncomplete(url, url) && url.metadataNextRetryAt !== null && url.metadataNextRetryAt > now) return false
  if (
    isLinkMetadataIncomplete(url, url)
    && url.metadataPolicyVersion === LINK_METADATA_POLICY_VERSION
    && hasExhaustedLinkMetadataRetries(url)
  ) return false
  const retryMs = linkMetadataRetryIntervalMs(url)
  return url.metadataPolicyVersion !== LINK_METADATA_POLICY_VERSION
    || url.fetchedAt === null || (
    isLinkMetadataIncomplete(url, url)
    && now - url.fetchedAt >= retryMs
  )
}

export function linkMetadataRetryDelayMs(url: UrlEntity, now = Date.now()): number | null {
  if (!isLinkMetadataIncomplete(url, url)) return null
  if (url.metadataNextRetryAt !== null) return Math.max(0, url.metadataNextRetryAt - now)
  if (
    url.metadataPolicyVersion === LINK_METADATA_POLICY_VERSION
    && hasExhaustedLinkMetadataRetries(url)
  ) return null
  if (url.fetchedAt === null || url.metadataPolicyVersion !== LINK_METADATA_POLICY_VERSION) return 0
  return Math.max(0, linkMetadataRetryIntervalMs(url) - (now - url.fetchedAt))
}

export function deriveLinkMetadataStatus(
  url: UrlEntity,
  now = Date.now(),
): LinkMetadataStatus | null {
  if (
    isLinkMetadataIncomplete(url, url)
    && url.metadataPolicyVersion === LINK_METADATA_POLICY_VERSION
    && hasExhaustedLinkMetadataRetries(url)
  ) return 'pending'
  if (isLinkMetadataIncomplete(url, url) && url.metadataNextRetryAt !== null && url.metadataNextRetryAt > now) {
    return 'waiting'
  }
  if (needsLinkMetadataRefresh(url, now)) return 'pending'
  return isLinkMetadataIncomplete(url, url) ? 'partial' : null
}

/**
 * Whether a link should show the loading shimmer: its details have never come back and the first read is still to be made or
 * under way. Once a read has finished (or failed, or the site refuses it) the link stays as it is: later background retries,
 * a missing picture, and a change of layout never bring the shimmer back.
 */
export function linkDetailsLoading(url: UrlEntity, status: LinkMetadataStatus | null = deriveLinkMetadataStatus(url)): boolean {
  return status !== null && url.fetchedAt === null && url.metadataFetchAttemptCount === 0 && !hasExhaustedLinkMetadataRetries(url)
}

export function enrichUrl(url: UrlEntity, metadata: LinkMetadata, fetchedAt = Date.now()): UrlEntity {
  const enriched = {
    ...url,
    title: metadata.title ?? url.title,
    description: metadata.description ?? url.description,
    imageUrl: metadata.imageUrl ?? url.imageUrl,
    durationSeconds: metadata.durationSeconds ?? url.durationSeconds,
    siteName: metadata.siteName ?? url.siteName,
    author: metadata.author ?? url.author,
    publishedAt: metadata.publishedAt ?? url.publishedAt,
    pageKind: metadata.pageKind ?? url.pageKind ?? null,
    fetchedAt,
    metadataPolicyVersion: LINK_METADATA_POLICY_VERSION,
  }
  if (!isLinkMetadataIncomplete(enriched, enriched)) return resetLinkMetadataRetry(enriched)

  const metadataFetchAttemptCount = Math.min(
    retryAttemptCountForCurrentPolicy(url) + 1,
    APP_SETTINGS.links.partialMetadataMaxAttempts,
  )
  return {
    ...enriched,
    metadataFetchAttemptCount,
    metadataNextRetryAt: metadataFetchAttemptCount >= APP_SETTINGS.links.partialMetadataMaxAttempts
      ? null
      : fetchedAt + metadataRetryBackoffMs(
        linkMetadataRetryIntervalMs(enriched),
        metadataFetchAttemptCount,
      ),
  }
}
